import React, { useState } from 'react'
import Moment from 'react-moment'
import { IETLlogs, ETLlog, ETLRunSteps, ETLStepDetail } from 'types'

interface ETLHistoryTimelineProps {
  allLogs: IETLlogs | undefined
  onRefresh: () => void
  onLoadRunSteps: (runId: string) => void
  runSteps: ETLRunSteps | null
  runStepsLoading: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  loadingMore?: boolean
}

const ETLHistoryTimeline: React.FC<ETLHistoryTimelineProps> = ({
  allLogs,
  onRefresh,
  onLoadRunSteps,
  runSteps,
  runStepsLoading,
  hasMore,
  onLoadMore,
  loadingMore,
}) => {
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'completed_with_errors' | 'partial'>('all')

  if (!allLogs) {
    return <div className="no-history">No ETL history available</div>
  }

  const runDates = Object.keys(allLogs).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  )

  // Helper functions (defined before use)
  const getRunStatus = (logs: ETLlog[]) => {
    const allCompleted = logs.every(log => log.run_time !== null)
    if (!allCompleted) return 'partial'
    const hasErrors = logs.some(log => log.sync_error === true)
    return hasErrors ? 'completed_with_errors' : 'completed'
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`
  }

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'success':
        return 'check_circle'
      case 'error':
        return 'error'
      case 'running':
        return 'sync'
      default:
        return 'radio_button_unchecked'
    }
  }

  const getStepClass = (status: string) => {
    return `timeline-step status-${status}`
  }

  // Filter runs based on search and status
  const filteredRunDates = runDates.filter((runDate) => {
    const logs = allLogs[runDate]
    const runStatus = getRunStatus(logs)

    // Status filter
    if (statusFilter !== 'all' && runStatus !== statusFilter) {
      return false
    }

    // Search filter (search in date or step names)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const dateMatches = runDate.toLowerCase().includes(searchLower)
      const stepMatches = logs.some(log =>
        log.step?.toLowerCase().includes(searchLower)
      )
      return dateMatches || stepMatches
    }

    return true
  })

  const toggleRun = (runDate: string) => {
    const newExpanded = new Set(expandedRuns)
    if (newExpanded.has(runDate)) {
      newExpanded.delete(runDate)
    } else {
      newExpanded.add(runDate)
      // Load detailed steps when expanding
      onLoadRunSteps(runDate)
    }
    setExpandedRuns(newExpanded)
  }

  return (
    <div className="etl-history-section">
      <div className="history-header">
        <h3>History</h3>
        <button onClick={onRefresh} className="refresh-button" title="Refresh history">
          <span className="material-icons">refresh</span>
        </button>
      </div>

      <div className="history-filters">
        <div className="search-box">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by date or step name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="clear-search"
              title="Clear search"
            >
              <span className="material-icons">close</span>
            </button>
          )}
        </div>

        <div className="status-filters">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            <span className="material-icons">check_circle</span>
            Completed
          </button>
          <button
            className={`filter-btn ${statusFilter === 'completed_with_errors' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed_with_errors')}
          >
            <span className="material-icons">warning</span>
            With Errors
          </button>
          <button
            className={`filter-btn ${statusFilter === 'partial' ? 'active' : ''}`}
            onClick={() => setStatusFilter('partial')}
          >
            <span className="material-icons">schedule</span>
            Partial
          </button>
        </div>
      </div>

      <div className="timeline-container">
        {filteredRunDates.length === 0 ? (
          <div className="no-results">
            <span className="material-icons">search_off</span>
            <p>No ETL runs match your search criteria</p>
            {(searchTerm || statusFilter !== 'all') && (
              <button onClick={() => { setSearchTerm(''); setStatusFilter('all') }} className="reset-filters-btn">
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {filteredRunDates.map((runDate) => {
              const logs = allLogs[runDate]
              const isExpanded = expandedRuns.has(runDate)
              const runStatus = getRunStatus(logs)
              const totalSteps = logs.length
              const totalDuration = logs.reduce((sum, log) => sum + (log.run_time || 0), 0)

              // Get detailed steps if available
              const detailedSteps = runSteps?.run_id === runDate ? runSteps.steps : null

              return (
                <div key={runDate} className="timeline-run">
              <div
                className={`run-summary ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleRun(runDate)}
              >
                <div className="run-icon">
                  <span className={`material-icons status-${runStatus}`}>
                    {runStatus === 'completed' ? 'check_circle' : runStatus === 'completed_with_errors' ? 'warning' : 'schedule'}
                  </span>
                </div>
                <div className="run-info">
                  <div className="run-title">
                    <span className="run-date">
                      <Moment format="MMM DD, YYYY [at] HH:mm:ss">{runDate}</Moment>
                    </span>
                    <span className={`run-status-badge status-${runStatus}`}>
                      {runStatus === 'completed_with_errors' ? 'completed with errors' : runStatus}
                    </span>
                  </div>
                  <div className="run-meta">
                    <span className="run-steps">
                      <span className="material-icons small">list</span>
                      {totalSteps} steps
                    </span>
                    <span className="run-duration">
                      <span className="material-icons small">schedule</span>
                      {formatDuration(totalDuration)}
                    </span>
                  </div>
                </div>
                <div className="expand-icon">
                  <span className="material-icons">
                    {isExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className="run-details">
                  {runStepsLoading && runSteps?.run_id !== runDate ? (
                    <div className="loading-steps">
                      <span className="material-icons sync-icon">sync</span>
                      <span>Loading step details...</span>
                    </div>
                  ) : detailedSteps ? (
                    <div className="timeline-steps">
                      {detailedSteps.map((step, index) => (
                        <div key={index} className={getStepClass(step.status)}>
                          <div className="step-indicator">
                            <span className={`material-icons step-icon status-${step.status}`}>
                              {getStepIcon(step.status)}
                            </span>
                            {index < detailedSteps.length - 1 && (
                              <div className="step-line"></div>
                            )}
                          </div>
                          <div className="step-content">
                            <div className="step-header">
                              <span className="step-name">{step.step}</span>
                              <span className="step-dataset">{step.dataset_name}</span>
                            </div>
                            <div className="step-timing">
                              <span className="step-start">
                                <Moment format="HH:mm:ss">{step.started_at}</Moment>
                              </span>
                              {step.duration_seconds !== undefined && (
                                <span className="step-duration">
                                  {formatDuration(step.duration_seconds)}
                                </span>
                              )}
                            </div>
                            {step.status === 'error' && !step.finished_at && (
                              <div className="step-error">
                                <span className="error-message">Step failed to start</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="basic-steps">
                      {logs.map((log, index) => (
                        <div key={index} className="basic-step">
                          <span className="basic-step-name">{log.step}</span>
                          <span className="basic-step-duration">
                            {formatDuration(log.run_time)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
                </div>
              )
            })}

            {hasMore && onLoadMore && (
              <div className="load-more-container">
                <button
                  onClick={onLoadMore}
                  className="load-more-button"
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="material-icons sync-icon">sync</span>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-icons">expand_more</span>
                      <span>Load More</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ETLHistoryTimeline
